import React, { useEffect, useState } from "react";
import axios from "axios";
import { deleteAnnouncement, getAnnouncementById } from "proxyUrl";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";

function DeleteAnnouncement() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canDeleteAnnouncement = userPermissions.includes("delete announcement");
  const [announcementData, setAnnouncementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchAnnouncementData = () => {
    axios
      .get(`${getAnnouncementById}${id}`, {
        withCredentials: true,
      })
      .then((response) => {
        if (response.data && response.data.data) {
          setAnnouncementData(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching announcement:", error);
        toast.error("Failed to fetch announcement data", {
          position: "top-center",
          autoClose: 2000,
        });
        navigate("/admin/announcements");
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const handleDelete = async () => {
    if (!canDeleteAnnouncement) {
      toast.error("You do not have permission to delete announcements");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this announcement?")) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.delete(`${deleteAnnouncement}${id}`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        toast.success("Announcement deleted successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        const delayRefresh = () => {
          navigate("/admin/announcements");
        };
        setTimeout(delayRefresh, 2100);
      }
    } catch (error) {
      console.error("Error deleting announcement:", error);
      if (error.response?.status === 404) {
        toast.error("Announcement not found!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error(
          error.response?.data?.error || "Failed to delete announcement!",
          {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canDeleteAnnouncement) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        You are not authorized to delete announcements.
      </div>
    );
  }

  if (fetching) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-gray-500">Loading announcement data...</div>
      </div>
    );
  }

  if (!announcementData) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-gray-500">Announcement not found</div>
      </div>
    );
  }

  return (
    <div className="my-10 flex h-[78vh] w-full flex-col items-center justify-center gap-5">
      {announcementData.image && (
        <div className="input relative mx-2 mr-7 flex items-center justify-center rounded-lg border-2 border-blueSecondary">
          <div className="h-[300px] w-[500px] rounded-lg">
            <img
              className="h-full w-full rounded-md object-cover"
              src={announcementData.image}
              alt={announcementData.title}
            />
          </div>
        </div>
      )}
      <h2 className="mx-10 mb-10 text-center text-5xl font-semibold text-indigo-600">
        {announcementData.title}
      </h2>
      <div className="mb-4 text-center text-gray-600 dark:text-gray-400">
        <p className="text-sm">
          Status: <span className="font-semibold">{announcementData.status}</span>
        </p>
        <p className="text-sm">
          Type: <span className="font-semibold">{announcementData.announcementType}</span>
        </p>
      </div>
      <button
        className="rounded-full text-xl hover:bg-red-500 duration-300 bg-red-400 px-10 py-5 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? "Deleting..." : "Delete Announcement"}
      </button>
      <ToastContainer />
    </div>
  );
}

export default DeleteAnnouncement;

